import * as React from 'react';
import { cn } from './cn';

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Primary + secondary buttons. */
  action?: React.ReactNode;
  size?: 'sm' | 'md';
  /** Dashed outline — for drop targets like the empty photo tray. */
  bordered?: boolean;
}

/**
 * The state a first-time guest sees most: no albums, no photos, no results.
 * Always says what to do next, never just "אין נתונים".
 */
export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { icon, title, description, action, size = 'md', bordered, className, children, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        size === 'sm' ? 'px-4 py-8' : 'px-6 py-14',
        bordered && 'rounded-xl border-2 border-dashed border-line-strong bg-paper-2/60',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span
          className={cn(
            'flex items-center justify-center rounded-full bg-paper-3 text-ink-3',
            size === 'sm' ? 'size-10 [&>svg]:size-5' : 'size-14 [&>svg]:size-7',
          )}
          aria-hidden
        >
          {icon}
        </span>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <p className={cn('font-display font-semibold text-ink', size === 'sm' ? 'text-sm' : 'text-lg')}>
          {title}
        </p>
        {description ? (
          <p className="max-w-sm text-pretty text-sm text-ink-2">{description}</p>
        ) : null}
      </div>
      {action ? <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div> : null}
      {children}
    </div>
  );
});
